'use client';

import React, { useEffect, useRef } from 'react';

interface HeroSectionProps {
  onBookClick: () => void;
}

export default function HeroSection({ onBookClick }: HeroSectionProps) {
  const headlineRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [headlineRef.current, subRef.current, ctaRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(40px)';
      setTimeout(() => {
        if (!el) return;
        el.style.transition = 'opacity 1.2s cubic-bezier(0.23,1,0.32,1), transform 1.2s cubic-bezier(0.23,1,0.32,1)';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 300 + i * 200);
    });
  }, []);

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-ink" aria-label="Hero">
      {/* Church photo background — black and white */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/assets/images/biserica_1__1_of_1_-1782890817335.jpg')`,
          filter: 'grayscale(100%)',
        }}
        aria-hidden="true"
      />
      {/* Dark overlay so text remains readable */}
      <div className="absolute inset-0 bg-ink/70" aria-hidden="true" />

      {/* Abstract animated gradient background */}
      <div className="absolute inset-0 hvz-hero-bg opacity-40" aria-hidden="true" />

      {/* Noise texture */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }}
        aria-hidden="true"
      />

      {/* Decorative abstract lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="hvz-line hvz-line-1" />
        <div className="hvz-line hvz-line-2" />
        <div className="hvz-line hvz-line-3" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end pb-16 px-6 md:px-10 lg:px-14">
        <div className="mb-3">
          <span className="font-mono text-[0.6rem] font-bold tracking-[0.4em] uppercase text-coral">
            Galați, Romania · Est. 2024
          </span>
        </div>

        <div ref={headlineRef} className="mb-8 max-w-[95vw]">
          <h1 className="hero-headline">
            TURNING<br />
            EVERY FRAME<br />
            INTO{' '}
            <span className="coral-word">EMOTION.</span>
          </h1>
        </div>

        <div ref={subRef} className="flex flex-col md:flex-row md:items-end gap-8 md:gap-16 max-w-4xl">
          <p className="font-sans text-base md:text-lg text-concrete/65 font-medium leading-relaxed max-w-sm">
            Seferis Toni — photographer based in Galați. Every session is a story waiting to be told through light, shadow, and raw feeling.
          </p>

          <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 shrink-0">
            <button
              onClick={onBookClick}
              className="btn-coral"
              aria-label="Book a session with Hvz.photos"
            >
              Book a Session
            </button>
            <a href="#work" className="btn-outline">
              See the Work ↓
            </a>
          </div>
        </div>

        <div className="mt-12 flex items-center gap-8 border-t border-concrete/10 pt-6">
          <div className="flex gap-8">
            {[
              { num: 'NEW', label: 'Fresh perspective' },
              { num: '∞', label: 'Frames of emotion' },
              { num: 'GL', label: 'Galați based' },
            ].map((s) => (
              <div key={s.label} className="flex flex-col gap-1">
                <span className="stat-num text-xl md:text-2xl text-concrete">{s.num}</span>
                <span className="section-label text-concrete/40">{s.label}</span>
              </div>
            ))}
          </div>
          <div className="hidden md:block ml-auto">
            <p className="font-mono text-[0.6rem] tracking-[0.3em] uppercase text-concrete/30">
              Scroll to explore ↓
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}